import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { SquareArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

import { useGetLicenseDetails, useUpdateLicence } from '../../repositories/licences-repository';
import type { LicenceServiceRequest, LicenceUpdatePayload } from '../../services/applications-services';
import { isEditableStatus, statusLabel } from '@/utils/application-status';
import { BusinessDetails, LicenceActivities, ServciesOffered } from '../fragments/icons';

import VerticalStepper from '../fragments/vertical-stepper';
import Button from '@/components/buttons/Button';
import BusinessForm from '../form/business-details-form';
import LicenceActivitiesForm from '../form/licence-activities-form';
import LicenceServicesOfferedForm from '../form/licence-services-offered-form';

/**
 * Editing a licence application the applicant already submitted.
 *
 * The wizard steps are the same components the create flow uses — imported, not
 * reimplemented — prefilled from GET /licence/{id} and saved with a PUT.
 *
 * Two steps of the create wizard are deliberately absent:
 *   - Documents. LicenseService.updateLicenseFields has no branch for
 *     `licenseDTO.getDocuments()`, so an uploaded file is silently ignored on
 *     update. Offering the upload here would look like a save that did nothing.
 *   - Summary. It exists to collect the licence fee and open the payment modal,
 *     which has nothing to do with correcting a submitted application.
 */

const STEP_FIELDS: Record<number, string[]> = {
    1: [
        'firstName', 'lastName', 'projectName', 'zone', 'region', 'district',
        'municipal', 'principalBusinessLocationPhoneNumber',
    ],
    2: ['selectedCategory'],
    3: ['selectedServices'],
};

/** Employment counts. No live wizard step edits these, but they still have to
 *  travel: updateLicenseFields replaces the whole employmentDetails block. */
const EMPLOYMENT_FIELDS = [
    'zanzibarisManagmentMale', 'zanzibarisManagmentFemale',
    'zanzibarisSkilledMale', 'zanzibarisSkilledFemale',
    'zanzibarisUnskilledMale', 'zanzibarisUnskilledFemale',
    'tanzaniaMainlandersManagmentMale', 'tanzaniaMainlandersManagmentFemale',
    'tanzaniaMainlandersSkilledMale', 'tanzaniaMainlandersSkilledFemale',
    'tanzaniaMainlandersUnskilledMale', 'tanzaniaMainlandersUnskilledFemale',
    'foreignersManagmentMale', 'foreignersManagmentFemale',
    'foreignersSkilledMale', 'foreignersSkilledFemale',
    'foreignersUnskilledMale', 'foreignersUnskilledFemale',
] as const;

/** "" for anything the record does not carry, so the field renders empty rather
 *  than as the string "undefined". Never a substitute value. */
function text(value: unknown): string {
    if (value === null || value === undefined) return '';
    return String(value);
}

function optionalText(value: unknown): string | undefined {
    if (value === null || value === undefined) return undefined;
    const trimmed = String(value).trim();
    return trimmed === '' ? undefined : trimmed;
}

function optionalNumber(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * The services step registers a hidden input whose value is the JSON of its
 * selection, so the submitted value can arrive as either an array or a string.
 */
function toServiceRequests(value: unknown): LicenceServiceRequest[] | undefined {
    let entries: unknown = value;
    if (typeof entries === 'string') {
        try {
            entries = JSON.parse(entries);
        } catch {
            return undefined;
        }
    }
    if (!Array.isArray(entries)) return undefined;

    const services: LicenceServiceRequest[] = [];
    entries.forEach((entry: any) => {
        const id = optionalNumber(entry?.id);
        if (id === undefined) return;
        services.push({ id, label: optionalText(entry?.label ?? entry?.name) });
    });
    return services.length > 0 ? services : undefined;
}

function toFormValues(licence: any) {
    const services = (licence?.selectedServices ?? []).map((service: any) => ({
        id: service?.id,
        label: text(service?.label ?? service?.name),
    }));

    return {
        projectName: text(licence?.projectName),
        physicalLocation: text(licence?.physicalLocation),
        businessName: text(licence?.businessName),

        zone: text(licence?.zone),
        region: text(licence?.region),
        district: text(licence?.district),
        municipal: text(licence?.municipal),
        street: text(licence?.street),
        principalBusinessLocationPostCode: text(licence?.principalBusinessLocationPostCode),
        principalBusinessLocationPoBox: text(licence?.principalBusinessLocationPoBox),
        principalBusinessLocationEmail: text(licence?.principalBusinessLocationEmail),
        principalBusinessLocationPhoneNumber: text(licence?.principalBusinessLocationPhoneNumber),
        principalBusinessWebsite: text(licence?.principalBusinessWebsite),

        selectedCategory: licence?.licenseCategory?.id ?? licence?.selectedCategory ?? '',
        selectedServices: services,
        licenseSubCategoryIds: services,
    };
}

function toPayload(values: any, licence: any): LicenceUpdatePayload {
    const employment: Record<string, number | undefined> = {};
    EMPLOYMENT_FIELDS.forEach((field) => {
        employment[field] = optionalNumber(licence?.[field]);
    });

    return {
        ...(employment as Partial<LicenceUpdatePayload>),

        projectName: values.projectName,
        physicalLocation: values.physicalLocation,
        businessName: values.businessName,

        // Carried by the record but not by any live wizard step. They are sent
        // back unchanged because the server replaces the whole businessDetail
        // block, so leaving them out would erase them.
        dateIssues: optionalText(licence?.dateIssues),
        previousLicenseNumber: optionalText(licence?.previousLicenseNumber),
        previousLicenseDateIssues: optionalText(licence?.previousLicenseDateIssues),
        nameOfWhichCurrentLicenseIsHeld: optionalText(licence?.nameOfWhichCurrentLicenseIsHeld),
        existingBuildings: optionalText(licence?.existingBuildings),

        zone: values.zone,
        region: values.region,
        district: values.district,
        municipal: values.municipal,
        street: values.street,
        principalBusinessLocationPostCode: values.principalBusinessLocationPostCode,
        principalBusinessLocationPoBox: values.principalBusinessLocationPoBox,

        principalBusinessLocationEmail: values.principalBusinessLocationEmail,
        principalBusinessLocationPhoneNumber: values.principalBusinessLocationPhoneNumber,
        principalBusinessWebsite: values.principalBusinessWebsite,

        selectedCategory: optionalNumber(values.selectedCategory),
        selectedServices: toServiceRequests(values.selectedServices),
    };
}

function LicenceEditForm({ applicationId, licence }: { applicationId: string; licence: any }) {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const detailsPath = `/applications/licences/applications-details/${applicationId}`;

    const { mutate, isPending } = useUpdateLicence(applicationId, () => navigate(detailsPath));

    const methods = useForm<any>({ defaultValues: toFormValues(licence) });

    const steps = [
        {
            step: 1,
            title: 'Business Details',
            isActive: currentStep === 1,
            isCompleted: currentStep > 1,
            image: <BusinessDetails isCompleted={currentStep > 1} isActive={currentStep === 1} />,
        },
        {
            step: 2,
            title: 'Licence Activities',
            isActive: currentStep === 2,
            isCompleted: currentStep > 2,
            image: <LicenceActivities isCompleted={currentStep > 2} isActive={currentStep === 2} />,
        },
        {
            step: 3,
            title: 'Servcies Offered',
            isActive: currentStep === 3,
            isCompleted: currentStep >= 3,
            image: <ServciesOffered isCompleted={currentStep >= 3} isActive={currentStep === 3} />,
        },
    ];

    const handleNext = () => {
        if (currentStep < steps.length) setCurrentStep((step) => step + 1);
    };

    const handleBack = () => {
        if (currentStep > 1) setCurrentStep((step) => step - 1);
    };

    const onSubmit: SubmitHandler<any> = (values) => {
        mutate(toPayload(values, licence));
    };

    /** Send the applicant to the step that actually holds the problem, rather
     *  than leaving them on a step with no visible error. */
    const onInvalid = (errors: Record<string, unknown>) => {
        const failing = Object.keys(errors);
        const step = Object.keys(STEP_FIELDS)
            .map(Number)
            .sort((a, b) => a - b)
            .find((candidate) => STEP_FIELDS[candidate].some((field) => failing.includes(field)));
        if (step) setCurrentStep(step);
        toast.error('Please fill all the required fields');
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return <BusinessForm />;
            case 2:
                return <LicenceActivitiesForm />;
            case 3:
                return <LicenceServicesOfferedForm />;
            default:
                return null;
        }
    };

    return (
        <div className="w-full">
            <div className="flex justify-between items-center border-b border-gray-200 px-4 py-2">
                <Link to={detailsPath}>
                    <Button
                        type="button"
                        intent="text"
                        leftIcon={<SquareArrowLeft className="size-5" />}
                        className="text-base text-secondary font-light"
                    >
                        Edit Application
                    </Button>
                </Link>
                <div className="flex justify-between items-center gap-3 my-1">
                    <Button
                        type="button"
                        intent="default"
                        size="md"
                        onClick={handleBack}>
                        Back
                    </Button>
                    <Button
                        type="button"
                        intent="primary"
                        className={`${currentStep === steps.length && 'hidden'}`}
                        size="md"
                        onClick={handleNext}>
                        Continue
                    </Button>
                </div>
            </div>
            <div className="flex space-x-5 gap-5 px-4">
                <div>
                    <VerticalStepper steps={steps} />
                </div>
                <div className="mt-2 ml-10 w-full max-w-3xl">
                    <FormProvider {...methods}>
                        <form className="w-full">
                            {renderStepContent()}
                            <div className="flex justify-end w-full gap-2 mt-3">
                                <Link to={detailsPath}>
                                    <Button type="button" intent="default" size="md">
                                        Cancel
                                    </Button>
                                </Link>
                                <Button
                                    type="button"
                                    intent="primary"
                                    loading={isPending}
                                    disabled={isPending}
                                    size="md"
                                    onClick={methods.handleSubmit(onSubmit, onInvalid)}>
                                    Save changes
                                </Button>
                            </div>
                        </form>
                    </FormProvider>
                </div>
            </div>
        </div>
    );
}

export default function LicenceApplicationEdit() {
    const { applicationId } = useParams();
    const { licence_details, isLoading } = useGetLicenseDetails(applicationId!);

    const licence = licence_details?.results ?? licence_details;

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
            </div>
        );
    }

    if (!licence) {
        return (
            <div className="px-4 py-6">
                <p className="text-sm text-gray-700">This application could not be loaded.</p>
            </div>
        );
    }

    if (!isEditableStatus(licence.applicationStatus)) {
        return (
            <div className="px-4 py-6">
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
                    <h1 className="text-base font-bold text-[#2C2C2C]">
                        This application can no longer be edited
                    </h1>
                    <p className="mt-1 text-sm text-amber-900">
                        It is {statusLabel(licence.applicationStatus) ?? 'in a closed state'}.
                    </p>
                    <Link to={`/applications/licences/applications-details/${applicationId}`}>
                        <Button type="button" intent="primary" size="md" className="mt-3 ml-0">
                            Back to application
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    return <LicenceEditForm applicationId={applicationId!} licence={licence} />;
}
